import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  ArrowRight, Check, Compass, Eye, ScrollText, Quote, Clock,
  Brain, Crosshair, CandlestickChart, ShieldCheck, Database, Network, BookOpen,
  Crown, Building2,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Logo } from "@/components/logo";
import { HeroChart } from "@/components/hero-chart";
import { getPublicContent, listPublishedFaqs } from "@/lib/admin-content.functions";
import { getCouncilInsights } from "@/lib/council-insights.functions";
import { getPerformanceStats } from "@/lib/performance.functions";
import { HomepageDisclaimer } from "@/components/disclaimer-banner";
import { supabase } from "@/integrations/supabase/client";
import { PLANS } from "@/lib/pricing/plans";



// Legend personas tied to each council agent. Shown on the homepage so visitors
// immediately see the premium positioning ("Soros framework", etc.) rather than
// just a generic role name.
const LEGEND_BY_AGENT: Record<string, string> = {
  "Macro Strategist": "The Macro Psychologist (Soros Framework)",
  "Trend Follower": "The Trend Disciple (Dennis / Turtle Framework)",
  "Mean Reversion Analyst": "The Statistical Contrarian (Simons Framework)",
  "Sentiment Analyst": "The Crowd Reader (Livermore Framework)",
  "Risk Manager": "The Risk Master (Dalio Framework)",
  "Liquidity Hunter": "The Order-Flow Hunter (Tudor Jones Framework)",
  "Volatility Analyst": "The Volatility Strategist (Taleb Framework)",
  "Structure Specialist": "The Market Structuralist (Wyckoff Framework)",
  "News Catalyst": "The News Trader (Druckenmiller Framework)",
  "Scalper": "The Tactical Scalper",
  "Devil's Advocate": "The Devil's Advocate (Counter-Thesis)",
};


export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "ForexMind AI — The AI Council Behind Smarter Market Decisions" },
      { name: "description", content: "11 specialist AI agents analyze every market setup through distinct macro, technical, sentiment, and risk frameworks. Transparent, institutional-style intelligence. Educational only — not financial advice." },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "ForexMind AI" },
      { property: "og:title", content: "ForexMind AI — AI Market Intelligence" },
      { property: "og:description", content: "Institutional-grade AI market intelligence. 11 specialist agents debate every setup. Educational insights only — not financial advice." },
      { property: "og:url", content: "https://forexmind.ai/" },
      { property: "og:image", content: "https://forexmind.ai/og-image.png" },
      { property: "og:image:secure_url", content: "https://forexmind.ai/og-image.png" },
      { property: "og:image:type", content: "image/png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@forexmindai" },
      { name: "twitter:title", content: "ForexMind AI — AI Market Intelligence" },
      { name: "twitter:description", content: "Institutional-grade AI market intelligence. 11 specialist agents debate every setup. Educational insights only — not financial advice." },
      { name: "twitter:image", content: "https://forexmind.ai/og-image.png" },
    ],
    links: [
      { rel: "canonical", href: "https://forexmind.ai/" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "ForexMind AI",
          applicationCategory: "FinanceApplication",
          operatingSystem: "Web",
          url: "https://forexmind.ai/",
          offers: [
            { "@type": "Offer", name: "Explorer", price: "0", priceCurrency: "USD" },
            { "@type": "Offer", name: "Analyst", price: "39", priceCurrency: "USD" },
            { "@type": "Offer", name: "Institutional", price: "99", priceCurrency: "USD" },
          ],
        }),
      },
    ],
  }),
  component: Landing,

});

const trustPillars = [
  {
    icon: Compass,
    title: "Methodology-Driven Intelligence",
    body:
      "Each specialist agent applies a distinct market framework inspired by proven schools of trading thought — from macro narrative and trend-following to sentiment and risk control.",
  },
  {
    icon: Eye,
    title: "Transparent Reasoning",
    body:
      "Every setup includes clear agent-by-agent reasoning, so users can see why the council reached its market view. No black box. No blind prompts.",
  },
  {
    icon: ScrollText,
    title: "Honest Performance",
    body:
      "We publish outcomes transparently — including wins, losses, and expired ideas — with public methodology and performance context.",
  },
];

const tiers = [
  {
    name: "Explorer",
    monthly: 0,
    annual: 0,
    annualTotal: 0,
    tag: "For exploring the platform",
    features: [
      "3 pairs (EUR/USD, GBP/USD, XAU/USD)",
      "Delayed market intelligence",
      "3 ideas per day",
      "Public performance page",
      "Limited council preview (3 of 11 agents)",
    ],
    cta: "Start free",
  },
  {
    name: PLANS.analyst.name,
    monthly: PLANS.analyst.monthlyUsd,
    annual: +(PLANS.analyst.annualUsd / 12).toFixed(2),
    annualTotal: PLANS.analyst.annualUsd,
    tag: "Most popular",
    highlight: true,
    features: [
      "10 pairs real-time",
      "50 ideas per day",
      "Full 11-agent council reasoning",
      "Telegram alerts",
      "Position calculator",
      "7-day free trial",
    ],
    cta: "Start 7-day trial",
  },
  {
    name: PLANS.institutional.name,
    monthly: PLANS.institutional.monthlyUsd,
    annual: +(PLANS.institutional.annualUsd / 12).toFixed(2),
    annualTotal: PLANS.institutional.annualUsd,
    tag: "For serious research workflows",
    features: [
      "All 16 pairs + priority XAU/USD",
      "500 ideas per day",
      "API access",
      "Backtesting + walk-forward engine",
      "Downloadable reports",
      "Event-mode coverage (NFP / FOMC / CPI)",
      "Council replay feature",
      "Priority support",
      "14-day free trial",
    ],
    cta: "Go Institutional",
  },
];

function Landing() {
  const getContent = useServerFn(getPublicContent);
  const getFaqs = useServerFn(listPublishedFaqs);
  const getInsights = useServerFn(getCouncilInsights);
  const getPerf = useServerFn(getPerformanceStats);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) {
      try {
        localStorage.setItem("forexmind_ref", ref.toUpperCase().trim().slice(0, 32));
      } catch {}
    }
  }, []);
  const { data: content } = useQuery({
    queryKey: ["public", "content"],
    queryFn: () => getContent(),
  });
  const { data: faqsData } = useQuery({
    queryKey: ["public", "faqs"],
    queryFn: () => getFaqs(),
  });
  const { data: insightsData } = useQuery({
    queryKey: ["public", "council-insights"],
    queryFn: () => getInsights(),
  });
  const { data: perfData } = useQuery({
    queryKey: ["public", "perf-stats"],
    queryFn: () => getPerf(),
  });
  const announcement = content?.announcement;
  const faqs = faqsData?.faqs ?? [];
  const insights = insightsData?.insights ?? [];

  // Hero metrics. We refuse to show fabricated marketing numbers — until the
  // backend reports real audited values we render an em-dash so the homepage
  // never lies. Numbers only appear after the minimum verified sample.
  const perfVerified = perfData?.status === "verified";
  const historicalAccuracy = perfVerified && perfData && perfData.winRatePct > 0
    ? `${perfData.winRatePct.toFixed(1)}%`
    : "—";
  const accuracySub = perfVerified
    ? `Based on ${perfData?.resolved ?? 0} verified market views · WAIT excluded`
    : "Track record builds after 30 resolved market views";
  const _confluenceRaw = (perfData as { avgConfluence?: number } | undefined)?.avgConfluence;
  const aiConfluence =
    perfVerified && typeof _confluenceRaw === "number" && _confluenceRaw > 0
      ? _confluenceRaw.toFixed(1)
      : "—";

  // Founding Member Lifetime spots — live scarcity counter from site_settings.
  const { data: spotsData } = useQuery({
    queryKey: ["public", "founding-spots"],
    queryFn: async () => {
      const { data } = await supabase
        .from("site_settings")
        .select("value")
        .eq("key", "founding_lifetime_spots")
        .maybeSingle();
      const v = (data?.value ?? {}) as { total?: number; claimed?: number };
      const total = typeof v.total === "number" ? v.total : 100;
      const claimed = typeof v.claimed === "number" ? v.claimed : 0;
      return { total, remaining: Math.max(0, total - claimed) };
    },
    staleTime: 30_000,
  });
  const foundingTotal = spotsData?.total ?? 100;
  const foundingRemaining = spotsData?.remaining ?? foundingTotal;
  const foundingFilledBars = Math.min(10, Math.max(0, Math.round((foundingRemaining / foundingTotal) * 10)));




  return (
    <main className="min-h-screen bg-background">
      {announcement?.active && announcement.text && (
        <div
          className={`px-6 py-2 text-center text-xs font-medium ${
            announcement.type === "warning"
              ? "bg-yellow-500/15 text-yellow-500"
              : announcement.type === "success"
                ? "bg-emerald-500/15 text-emerald-500"
                : "bg-primary/15 text-primary"
          }`}
        >
          {announcement.text}
        </div>
      )}
      <HomepageDisclaimer />

      {/* Nav — in-flow so it never overlaps the disclaimer banner */}
      <header className="relative z-20 flex items-center justify-between gap-3 px-4 py-3 sm:px-6 sm:py-4">
        <Logo size="sm" />
        <div className="flex items-center gap-2 sm:gap-3">
          <Button asChild variant="ghost" size="sm" className="px-2 text-xs text-foreground/90 sm:px-3 sm:text-sm">
            <Link to="/auth">Sign in</Link>
          </Button>
          <Button asChild size="sm" className="rounded-full bg-primary px-3 text-xs text-primary-foreground hover:bg-primary/90 sm:px-4 sm:text-sm">
            <Link to="/auth">Get started</Link>
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section
        className="relative overflow-hidden pb-0 pt-10 sm:pt-16"
        style={{
          background:
            "radial-gradient(ellipse 80% 60% at 50% 30%, hsl(200 80% 18% / 0.55), transparent 70%), linear-gradient(180deg, #0a1428 0%, #050a18 100%)",
        }}
      >
        {/* Hexagon grid pattern */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.18]"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='56' height='64' viewBox='0 0 56 64'><path d='M28 1L54 16v32L28 63 2 48V16z' fill='none' stroke='%2300D4FF' stroke-width='0.6' opacity='0.55'/></svg>\")",
            backgroundSize: "56px 64px",
          }}
        />
        {/* Soft top glow */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-[420px]"
          style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(0,212,255,0.18), transparent 70%)" }}
        />

        <div className="relative mx-auto max-w-6xl px-6">
          {/* Headline block */}
          <div className="text-center">
            <h1 className="brand-text text-5xl font-bold leading-[1.05] tracking-tight md:text-7xl">
              <span className="bg-gradient-to-b from-[#7FE7FF] to-[#0099CC] bg-clip-text text-transparent drop-shadow-[0_0_30px_rgba(0,212,255,0.45)]">
                AI-Powered
              </span>{" "}
              <span className="text-foreground">Forex</span>
              <br />
              <span className="text-foreground">Market Intelligence</span>
            </h1>
            <p className="mx-auto mt-7 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
              11-agent council analysis. Transparent reasoning. Educational insights only.
            </p>

            <div className="mt-9 flex flex-wrap justify-center gap-3">
              <Button asChild size="lg" className="rounded-full bg-primary px-7 text-primary-foreground shadow-[0_0_40px_-10px_rgba(0,212,255,0.7)] hover:bg-primary/90">
                <Link to="/auth">Get started <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-full border-primary/30 bg-transparent text-foreground hover:bg-primary/10">
                <Link to="/performance">View Performance</Link>
              </Button>
            </div>
          </div>

          {/* Live animated chart */}
          <div className="relative mx-auto mt-12 max-w-5xl">
            <HeroChart />

            {/* Glassy stats panel overlapping the chart base */}
            <div className="relative z-10 mx-auto -mt-10 max-w-4xl rounded-2xl border border-primary/20 bg-[#0a1428]/85 px-6 py-5 backdrop-blur-md shadow-[0_30px_80px_-30px_rgba(0,212,255,0.35)]">
              <div className="grid grid-cols-1 divide-y divide-border/40 xl:grid-cols-3 xl:divide-x xl:divide-y-0">
                {[
                  {
                    icon: Brain,
                    value: aiConfluence,
                    label: "AI Confluence",
                    sub: "Multi-Agent Consensus",
                  },
                  {
                    icon: Crosshair,
                    value: historicalAccuracy,
                    label: "Historical Accuracy*",
                    sub: accuracySub,

                  },
                  {
                    icon: CandlestickChart,
                    value: "EUR/USD · GBP/JPY",
                    label: "Major FX Pairs + Gold",
                    sub: "Data-Rich Coverage",
                  },
                ].map((s) => (
                  <div key={s.label} className="flex items-center gap-3 px-3 py-3 sm:gap-4 sm:px-4 xl:px-6">
                    <s.icon className="h-7 w-7 shrink-0 text-primary sm:h-9 sm:w-9" strokeWidth={1.2} />
                    <div className="min-w-0 text-left">
                      <div className="brand-text truncate text-lg font-semibold tracking-tight text-primary sm:text-2xl xl:text-[26px]">
                        {s.value}
                      </div>
                      <div className="mt-0.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-foreground/85">
                        {s.label}
                      </div>
                      <div className="text-[11px] text-muted-foreground">{s.sub}</div>
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-3 px-2 text-center text-[10px] leading-relaxed text-muted-foreground/80">
                *Historical accuracy is based on backtested performance and excludes WAIT/neutral views. Past performance does not guarantee future results.
              </p>
            </div>
          </div>
        </div>


        {/* Bottom value strip */}
        <div className="relative mt-16 border-t border-border/40 bg-[#050a18]/70">
          <div className="mx-auto grid max-w-6xl grid-cols-2 gap-px md:grid-cols-4">
            {[
              { icon: ShieldCheck, kicker: "Transparent", body: "Explainable AI reasoning" },
              { icon: Database, kicker: "Data-First", body: "Institutional-grade data" },
              { icon: Network, kicker: "Structured", body: "Multi-agent intelligence" },
              { icon: BookOpen, kicker: "Educational", body: "Insights for learning" },
            ].map((p) => (
              <div key={p.kicker} className="flex items-center gap-3 px-6 py-5">
                <p.icon className="h-5 w-5 text-primary" strokeWidth={1.4} />
                <div>
                  <div className="brand-text text-xs font-semibold uppercase tracking-[0.22em] text-foreground">
                    {p.kicker}
                  </div>
                  <div className="text-[11px] text-muted-foreground">{p.body}</div>
                </div>
              </div>
            ))}
          </div>
          <p className="px-6 pb-5 pt-3 text-center text-[11px] text-muted-foreground/80">
            ForexMind AI provides educational market analysis and research tools. Not financial advice.
          </p>
        </div>
      </section>




      {/* Trust section */}
      <section className="border-t border-border/60 py-24">
        <div className="mx-auto max-w-6xl px-6">
          <div className="mb-14 max-w-2xl">
            <div className="mb-3 text-[10px] uppercase tracking-[0.24em] text-primary/80">Why ForexMind</div>
            <h2 className="brand-text text-3xl font-semibold tracking-tight md:text-4xl">
              Why ForexMind AI Feels Different
            </h2>
          </div>
          <div className="grid gap-px overflow-hidden rounded-xl border border-border/60 bg-border/60 md:grid-cols-3">
            {trustPillars.map((p) => (
              <div key={p.title} className="flex flex-col gap-4 bg-card/60 p-8">
                <p.icon className="h-5 w-5 text-primary" strokeWidth={1.5} />
                <h3 className="brand-text text-base font-semibold tracking-tight text-foreground">
                  {p.title}
                </h3>
                <p className="text-sm leading-relaxed text-muted-foreground">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <PricingSection />


      {/* What the Council Is Seeing */}
      <section className="border-t border-border/60 py-24">
        <div className="mx-auto max-w-6xl px-6">
          <div className="mb-12 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
            <div>
              <div className="mb-3 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-primary/80">
                <span className="h-1 w-1 animate-pulse rounded-full bg-primary" />
                Live intelligence
              </div>
              <h2 className="brand-text text-3xl font-semibold tracking-tight md:text-4xl">
                What the Council Is Seeing
              </h2>
              <p className="mt-3 max-w-xl text-sm text-muted-foreground">
                Rotating excerpts from recent specialist-agent reasoning. Educational context — not trade instructions.
              </p>
            </div>
            <Link to="/performance" className="text-xs uppercase tracking-[0.18em] text-primary hover:underline">
              View methodology →
            </Link>
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {insights.map((it, i) => (
              <Card key={`${it.pair}-${it.agent}-${i}`} className="flex flex-col gap-4 border-border/60 bg-card/70 p-6">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs font-semibold tracking-wider text-primary">
                    {it.pair.slice(0, 3)}/{it.pair.slice(3)}
                  </span>
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                    <Clock className="h-3 w-3" /> {it.ageMins < 60 ? `${it.ageMins}m` : `${Math.round(it.ageMins / 60)}h`} ago
                  </span>
                </div>
                <div className="relative flex-1 text-sm leading-relaxed text-foreground/90">
                  <Quote className="absolute -left-1 -top-1 h-3 w-3 text-primary/40" />
                  <span className="pl-3 italic">{it.excerpt}</span>
                </div>
                <div className="border-t border-border/60 pt-3">
                  <div className="brand-text text-xs font-semibold leading-snug tracking-tight text-foreground">
                    {LEGEND_BY_AGENT[it.agent] ?? it.agent}
                  </div>

                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{it.framework}</div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      {faqs.length > 0 && (
        <section className="border-t border-border/60 py-20">
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                mainEntity: faqs.map((f) => ({
                  "@type": "Question",
                  name: f.question,
                  acceptedAnswer: { "@type": "Answer", text: f.answer },
                })),
              }),
            }}
          />
          <div className="mx-auto max-w-3xl px-6">
            <div className="mb-10 text-center">
              <h2 className="brand-text text-3xl font-semibold tracking-tight md:text-4xl">Questions, answered.</h2>
              <p className="mt-3 text-sm text-muted-foreground">Everything you need to know before signing up.</p>
            </div>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((f) => (
                <AccordionItem key={f.id} value={f.id} className="border-border/60">
                  <AccordionTrigger className="text-left text-base font-medium">
                    {f.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground whitespace-pre-line">
                    {f.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="border-t border-border/60 py-20 text-center">
        <h2 className="brand-text text-3xl font-semibold tracking-tight md:text-4xl">
          Ready to see what the council sees?
        </h2>
        <p className="mt-3 text-sm text-muted-foreground">Educational market intelligence only. Not financial advice.</p>
        <Button asChild size="lg" className="mt-8 bg-primary text-primary-foreground hover:bg-primary/90">
          <Link to="/auth">Access the Council <ArrowRight className="ml-2 h-4 w-4" /></Link>
        </Button>
      </section>
    </main>
  );
}

function PricingSection() {
  const [annual, setAnnual] = useState(true);
  const { data: spotsData } = useQuery({
    queryKey: ["public", "founding-spots"],
    queryFn: async () => {
      const { data } = await supabase
        .from("site_settings")
        .select("value")
        .eq("key", "founding_lifetime_spots")
        .maybeSingle();
      const v = (data?.value ?? {}) as { total?: number; claimed?: number };
      const total = typeof v.total === "number" ? v.total : 100;
      const claimed = typeof v.claimed === "number" ? v.claimed : 0;
      return { total, remaining: Math.max(0, total - claimed) };
    },
    staleTime: 30_000,
  });
  const foundingTotal = spotsData?.total ?? 100;
  const foundingRemaining = spotsData?.remaining ?? foundingTotal;
  const foundingFilledBars = Math.min(
    10,
    Math.max(0, Math.round((foundingRemaining / foundingTotal) * 10)),
  );
  return (

    <section className="border-t border-border/60 bg-card/20 py-24">
      <div className="mx-auto max-w-6xl px-6">
        <div className="mb-8 text-center">
          <h2 className="brand-text text-3xl font-semibold tracking-tight md:text-4xl">Choose your access tier</h2>
          <p className="mx-auto mt-3 max-w-xl text-sm text-muted-foreground">
            All plans provide educational market intelligence only. Not financial advice.
          </p>
          <div className="mt-6 inline-flex items-center rounded-full border border-border/60 bg-card/60 p-1 text-xs font-bold">
            <button
              onClick={() => setAnnual(false)}
              className={`rounded-full px-4 py-1.5 transition-colors ${!annual ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
            >
              Monthly
            </button>
            <button
              onClick={() => setAnnual(true)}
              className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 transition-colors ${annual ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
            >
              Annual
              <span className={`rounded-full px-1.5 py-0.5 text-[9px] ${annual ? "bg-primary-foreground/20 text-primary-foreground" : "bg-buy/20 text-buy"}`}>
                SAVE 2 MONTHS
              </span>
            </button>
          </div>
          {annual && (
            <p className="mt-2 text-[11px] text-muted-foreground">Save 2 months with annual billing.</p>
          )}
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {tiers.map((t) => {
            const isFree = t.monthly === 0;
            const displayPrice = isFree ? 0 : annual ? t.annual : t.monthly;
            const priceLabel = isFree
              ? "$0"
              : `$${Number.isInteger(displayPrice) ? displayPrice : displayPrice.toFixed(2)}`;
            return (
              <Card
                key={t.name}
                className={`relative flex flex-col border-border/60 bg-card/80 p-6 sm:p-8 ${t.highlight ? "border-primary/50 md:order-none order-first" : t.name === "Explorer" ? "md:order-none order-2" : "md:order-none order-3"} ${t.highlight ? "border-primary/50" : ""}`}
              >
                {t.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-primary-foreground">
                    {t.tag}
                  </div>
                )}
                <div className="mb-1 text-xs uppercase tracking-[0.18em] text-muted-foreground">
                  {!t.highlight && t.tag}
                  {t.highlight && "Tier"}
                </div>
                <div className="brand-text text-xl font-semibold tracking-tight">{t.name}</div>
                <div className="mt-3 font-display text-5xl font-semibold tracking-tight">
                  {priceLabel}
                  <span className="text-base font-normal text-muted-foreground">/mo</span>
                </div>
                {!isFree && annual && (
                  <div className="mt-1 text-[10px] uppercase tracking-widest text-buy">
                    ${t.annualTotal}/yr · save 2 months
                  </div>
                )}
                <ul className="mt-6 flex-1 space-y-3 text-sm">
                  {t.features.map((feat) => (
                    <li key={feat} className="flex items-start gap-2">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" strokeWidth={2} />
                      <span className="text-muted-foreground">{feat}</span>
                    </li>
                  ))}
                </ul>

                {t.name === "Institutional" && (
                  <div className="mt-6 rounded-lg border border-amber-500/40 bg-gradient-to-br from-amber-500/10 to-transparent p-3">
                    <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-amber-400">
                      <Crown className="h-3 w-3" /> Founding Member Offer
                    </div>
                    <div className="mt-1 flex items-baseline gap-1.5">
                      <span className="font-display text-xl font-bold">$799</span>
                      <span className="text-[11px] text-muted-foreground">one-time · lifetime</span>
                    </div>
                    <p className="mt-2 text-[11px] text-muted-foreground">
                      Lock in forever — only {foundingTotal} spots.
                    </p>
                    <div className="mt-2 flex items-center gap-2">
                      <div className="flex h-2 flex-1 overflow-hidden rounded-full bg-amber-500/10">
                        {Array.from({ length: 10 }).map((_, i) => (
                          <div
                            key={i}
                            className={`h-full flex-1 ${i > 0 ? "ml-0.5" : ""} ${
                              i < foundingFilledBars ? "bg-amber-400" : "bg-amber-500/15"
                            }`}
                          />
                        ))}
                      </div>
                      <span className="font-mono text-[10px] font-semibold text-amber-300">
                        {foundingRemaining} left
                      </span>
                    </div>

                  </div>
                )}

                <Button
                  asChild
                  className={`mt-8 ${t.highlight ? "bg-primary text-primary-foreground hover:bg-primary/90" : ""}`}
                  variant={t.highlight ? "default" : "outline"}
                >
                  <Link to="/auth">{t.cta}</Link>
                </Button>
              </Card>
            );
          })}
        </div>

        {/* Partner tier */}
        <div className="mt-6">
          <Card className="flex flex-col items-start gap-4 border-dashed border-border/60 bg-card/40 p-6 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-3">
              <Building2 className="mt-0.5 h-6 w-6 text-primary" />
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-display text-lg font-bold">Partner</h3>
                  <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-primary">
                    Coming soon
                  </span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  For communities, educators, and prop firms — from{" "}
                  <span className="font-semibold text-foreground">$499/month</span>.
                </p>
              </div>
            </div>
            <Button asChild variant="outline">
              <Link to="/support">Join waitlist →</Link>
            </Button>
          </Card>
        </div>
      </div>
    </section>
  );
}

